/**
 * AuditLogTable - Sortable table of audit log entries
 *
 * Features:
 * - Sortable columns (timestamp, action, user, entity)
 * - Clickable rows to view details
 * - Action type badges with icons
 * - User avatar and info
 * - Responsive design
 */
import { cn } from '@respectly/ui';
import { Card, CardContent } from '@respectly/ui';
import { motion } from 'framer-motion';
import {
  LogIn,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Ban,
  RefreshCw,
  XCircle,
  UserCog,
  Key,
  Settings,
  Trash,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileText,
  Clock,
  Globe,
} from 'lucide-react';
import type { SuperAdminAuditLog } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface AuditLogTableProps {
  logs: SuperAdminAuditLog[];
  isLoading?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  onRowClick?: (log: SuperAdminAuditLog) => void;
  className?: string;
}

// ============================================
// Constants
// ============================================

const ACTION_CONFIG: Record<
  string,
  { icon: React.ElementType; color: string; bgColor: string; label: string }
> = {
  login: {
    icon: LogIn,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    label: 'Login',
  },
  logout: {
    icon: LogOut,
    color: 'text-slate-600 dark:text-slate-400',
    bgColor: 'bg-slate-100 dark:bg-slate-800',
    label: 'Logout',
  },
  create: {
    icon: Plus,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
    label: 'Create',
  },
  update: {
    icon: Pencil,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    label: 'Update',
  },
  delete: {
    icon: Trash2,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    label: 'Delete',
  },
  suspend: {
    icon: Ban,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    label: 'Suspend',
  },
  reactivate: {
    icon: RefreshCw,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
    label: 'Reactivate',
  },
  cancel: {
    icon: XCircle,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    label: 'Cancel',
  },
  impersonate: {
    icon: UserCog,
    color: 'text-violet-600 dark:text-violet-400',
    bgColor: 'bg-violet-50 dark:bg-violet-900/20',
    label: 'Impersonate',
  },
  password_reset: {
    icon: Key,
    color: 'text-cyan-600 dark:text-cyan-400',
    bgColor: 'bg-cyan-50 dark:bg-cyan-900/20',
    label: 'Password Reset',
  },
  toggle_maintenance: {
    icon: Settings,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    label: 'Maintenance',
  },
  clear_cache: {
    icon: Trash,
    color: 'text-slate-600 dark:text-slate-400',
    bgColor: 'bg-slate-100 dark:bg-slate-800',
    label: 'Clear Cache',
  },
};

const ENTITY_ICONS: Record<string, React.ElementType> = {
  tenant: FileText,
  user: UserCog,
  plan: FileText,
  template: FileText,
  super_admin_user: UserCog,
  feature_flag: Settings,
  platform: Globe,
};

// ============================================
// Helpers
// ============================================

function formatTimestamp(date: Date): { date: string; time: string } {
  const d = new Date(date);
  return {
    date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  };
}

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function getEntityDisplayName(entityType: string): string {
  const names: Record<string, string> = {
    tenant: 'Account',
    user: 'User',
    plan: 'Plan',
    template: 'Template',
    super_admin_user: 'Admin User',
    feature_flag: 'Feature Flag',
    platform: 'Platform',
  };
  return names[entityType] || entityType;
}

// ============================================
// Component
// ============================================

export function AuditLogTable({
  logs,
  isLoading = false,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  className,
}: AuditLogTableProps) {
  const renderSortIcon = (field: string) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" />;
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-3.5 w-3.5 text-blue-500" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5 text-blue-500" />
    );
  };

  if (isLoading) {
    return (
      <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4 animate-pulse">
                <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded" />
                  <div className="h-3 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
                </div>
                <div className="h-6 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (logs.length === 0) {
    return (
      <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="h-14 w-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
            <FileText className="h-7 w-7 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
            No Audit Logs Found
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
            No audit logs match your current filters. Try adjusting your search criteria.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60 overflow-hidden', className)}>
      <CardContent className="p-0">
        {/* Header */}
        <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
          <button
            className="col-span-2 flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            onClick={() => onSort?.('createdAt')}
          >
            Timestamp
            {renderSortIcon('createdAt')}
          </button>
          <button
            className="col-span-2 flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            onClick={() => onSort?.('action')}
          >
            Action
            {renderSortIcon('action')}
          </button>
          <div className="col-span-3 text-xs font-medium text-slate-600 dark:text-slate-400">
            User
          </div>
          <div className="col-span-3 text-xs font-medium text-slate-600 dark:text-slate-400">
            Entity
          </div>
          <div className="col-span-2 text-xs font-medium text-slate-600 dark:text-slate-400">
            IP Address
          </div>
        </div>

        {/* Rows */}
        <div className="divide-y divide-slate-200 dark:divide-slate-700">
          {logs.map((log, index) => {
            const actionConfig = ACTION_CONFIG[log.action] || {
              icon: FileText,
              color: 'text-slate-600 dark:text-slate-400',
              bgColor: 'bg-slate-100 dark:bg-slate-800',
              label: log.action,
            };
            const ActionIcon = actionConfig.icon;
            const EntityIcon = ENTITY_ICONS[log.entityType] || FileText;
            const timestamp = formatTimestamp(log.createdAt);

            return (
              <motion.div
                key={log.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.02 }}
                className={cn(
                  'grid grid-cols-12 gap-4 px-4 py-3.5 items-center',
                  'hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors',
                  onRowClick && 'cursor-pointer'
                )}
                onClick={() => onRowClick?.(log)}
              >
                {/* Timestamp */}
                <div className="col-span-6 md:col-span-2 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400 hidden sm:block" />
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {timestamp.date}
                    </p>
                    <p className="text-xs text-slate-500">{timestamp.time}</p>
                  </div>
                </div>

                {/* Action */}
                <div className="col-span-6 md:col-span-2">
                  <div
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full',
                      actionConfig.bgColor
                    )}
                  >
                    <ActionIcon className={cn('h-3.5 w-3.5', actionConfig.color)} />
                    <span className={cn('text-xs font-medium', actionConfig.color)}>
                      {actionConfig.label}
                    </span>
                  </div>
                </div>

                {/* User */}
                <div className="col-span-6 md:col-span-3 flex items-center gap-2">
                  {log.user ? (
                    <>
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-white">
                          {getInitials(log.user.firstName, log.user.lastName)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {log.user.firstName} {log.user.lastName}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{log.user.email}</p>
                      </div>
                    </>
                  ) : (
                    <span className="text-sm text-slate-400">System</span>
                  )}
                </div>

                {/* Entity */}
                <div className="col-span-6 md:col-span-3 flex items-center gap-2 min-w-0">
                  <div className="h-7 w-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                    <EntityIcon className="h-3.5 w-3.5 text-slate-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500">
                      {getEntityDisplayName(log.entityType)}
                    </p>
                    {log.entityName && (
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                        {log.entityName}
                      </p>
                    )}
                  </div>
                </div>

                {/* IP Address */}
                <div className="hidden md:flex col-span-2 items-center justify-between">
                  <span className="text-sm text-slate-500 font-mono">
                    {log.ipAddress || '—'}
                  </span>
                  {onRowClick && (
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
